import { Component, OnInit, OnDestroy, ViewChild, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SkeletonModule } from 'primeng/skeleton';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';

import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DropdownModule } from 'primeng/dropdown';
import { ChartModule } from 'primeng/chart';

import { AuthService } from '../../../../services/account/auth.service';
import { OperationsService } from '../../../../services/operations/operations.service';
import {
  StudentDocumentVaultResponse, 
  DocumentCategoryEnum,
  DocumentTypeEnum,
  EssayTypeEnum,
  DocumentStatusEnum
} from '../../../../models/student-document-vault/student-document-vault';
import { CourseLevelEnumDescriptions } from '../../../../models/enums/courseLevelEnum';

import { RequestMissingDocumentModalComponent } from './request-missing-document-modal/request-missing-document-modal.component';

@Component({
  selector: 'app-operations-student-document-vault',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    SkeletonModule,
    AutoCompleteModule,
    FormsModule,
    TableModule,
    DialogModule,
    TagModule,
    TooltipModule,
    DropdownModule,
    ChartModule,
    RequestMissingDocumentModalComponent
  ],
  providers: [ConfirmationService],
  templateUrl: './document-vault.component.html',
  styleUrls: ['./document-vault.component.scss']
})
export class OperationsStudentDocumentVaultComponent implements OnInit, OnDestroy {
  @Input() isMentorView: boolean = false;
  @ViewChild('requestModal') requestModal!: RequestMissingDocumentModalComponent;
  
  // State variables
  private destroy$ = new Subject<void>();
  mobileSidebarVisible: boolean = false;
  @Input() studentId: string = '';
  
  // Table Data
  documents: StudentDocumentVaultResponse[] = [];
  isLoadingDocuments: boolean = false;
  totalRecords: number = 0;
  
  // Dialog State
  selectedDocument: StudentDocumentVaultResponse | null = null;
  showDetailsDialog: boolean = false;
  isLoadingDetails: boolean = false;

  // Filter State
  activeStatusFilter: number = 1; // 1 = All, 2 = Uploaded, 3 = Missing
  statusCounts: any = { all: 0, uploaded: 0, missing: 0 };

  // Enums for template
  DocumentCategoryEnum = DocumentCategoryEnum;
  DocumentTypeEnum = DocumentTypeEnum;
  DocumentStatusEnum = DocumentStatusEnum;
  EssayTypeEnum = EssayTypeEnum;

  categories = [
    { label: 'All Categories', value: null },
    { label: 'Identity', value: DocumentCategoryEnum.Identity },
    { label: 'Academics', value: DocumentCategoryEnum.Academics },
    { label: 'Essays', value: DocumentCategoryEnum.EssayPilot },
    { label: 'LOR', value: DocumentCategoryEnum.LOR },
    { label: 'Test Scores', value: DocumentCategoryEnum.TestScores },
    { label: 'Other', value: DocumentCategoryEnum.Other }
  ];

  statusOptions = [
    { label: 'All', value: 1 },
    { label: 'Uploaded', value: 2 },
    { label: 'Missing', value: 3 }
  ];


  constructor(
    private operationsService: OperationsService,
    private authService: AuthService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.studentId) {
      this.loadStatusCounts();
      this.loadAllDocuments();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Data Loading Methods ---


  loadStatusCounts(): void {
    this.operationsService.getStudentDocumentVaultStatusCounts(this.studentId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        const data = res?.responseData ?? res?.result ?? {};
        this.statusCounts = {
            all: data.all ?? 0,
            uploaded: data.uploaded ?? data.uploadedCount ?? 0,
            missing: data.missing ?? data.missingCount ?? 0
        };
      },
      error: () => {
        // Fallback or silent error
      }
    });
  }

  loadAllDocuments(event?: any): void {
    this.isLoadingDocuments = true;
    
    // Extract pagination
    const page = event?.rows ? (Math.floor(event.first / event.rows)) + 1 : 1;
    const rows = event?.rows || 10;
    
    // Extract filters
    const category = this.extractFilterValue(event, 'category', null);
    const statusFilter = this.extractFilterValue(event, 'statusFilter', 1);
    
    // Extract sort state
    const sortField = event?.sortField;
    const sortOrder = event?.sortOrder; // 1 = asc, -1 = desc

    if (!this.studentId) {
      this.isLoadingDocuments = false;
      return;
    }

    this.operationsService.getStudentDocumentVault(this.studentId, page, rows, category, statusFilter, sortField, sortOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.documents = res?.responseData ?? res?.result ?? [];
          this.totalRecords = res?.totalCount ?? res?.total ?? this.documents.length;
          this.isLoadingDocuments = false;
        },
        error: () => {
          this.isLoadingDocuments = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load documents' });
        }
      });
  }

  // --- Helper Methods ---

  /** Safe extraction of PrimeNG filter values */
  private extractFilterValue(event: any, fieldName: string, defaultValue: any): any {
    if (!event?.filters || !event.filters[fieldName]) {
      return defaultValue;
    }
    
    const filterObj = event.filters[fieldName];
    const val = Array.isArray(filterObj) ? filterObj[0].value : filterObj.value;
    
    return (val !== null && val !== undefined) ? val : defaultValue;
  }


  viewDocumentDetails(id: string): void {
    this.isLoadingDetails = true;
    this.selectedDocument = null;
    this.showDetailsDialog = true;

    this.operationsService.getDocumentById(id, this.studentId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.selectedDocument = res?.responseData ?? res?.result ?? res;
        this.isLoadingDetails = false;
      },
      error: () => {
        this.isLoadingDetails = false;
        this.showDetailsDialog = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load document details' });
      }
    });
  }

  downloadDocument(path: string, title: string): void {
    if (!path) return;
    const link = document.createElement('a');
    link.href = path;
    link.target = '_blank';
    link.download = title;
    link.click();
  }

  refreshDocuments(): void {
    this.loadAllDocuments();
    this.loadStatusCounts();
  }

  triggerRequestModal(category?: DocumentCategoryEnum): void {
    if (!this.studentId) {
      this.messageService.add({ severity: 'warn', summary: 'Missing Student ID', detail: 'Cannot request document without student identification.' });
      return;
    }

    if (this.requestModal) {
      this.requestModal.show(this.studentId, category);
    } else {
      this.messageService.add({ severity: 'error', summary: 'System Error', detail: 'Request modal component not initialized. Please refresh.' });
    }
  }


  getCategoryLabel(category: DocumentCategoryEnum): string {
    switch (category) {
      case DocumentCategoryEnum.EssayPilot: return 'Essays';
      case DocumentCategoryEnum.LOR: return 'LOR';
      case DocumentCategoryEnum.Identity: return 'Identity';
      case DocumentCategoryEnum.Academics: return 'Academics';
      case DocumentCategoryEnum.TestScores: return 'Test Scores';
      case DocumentCategoryEnum.Other: return 'Other';
      default: return 'Other';
    }
  }


}
